import { FC, useEffect, useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import clsx from 'clsx';
import { GroupDiscount } from '../core/_models';
import { useListView } from '../core/ListViewProvider';
import { GroupDiscountsListLoading } from '../components/loading/GroupDiscountsListLoading';
import { createGroupDiscount, updateGroupDiscount } from '../core/_requests';
import { useQueryResponse } from '../core/QueryResponseProvider';
import { FormattedMessage } from 'react-intl';
import axios from 'axios';
import { Service } from '../../../service/services-list/core/_models';
import { Bundle } from '../../../bundle/bundles-list/core/_models';
import { ResellerGroup } from '../../../reseller-group/resellers-groups-list/core/_models';

const editGroupDiscountSchema = Yup.object().shape({
  reseller_group_id: Yup.number().required('Reseller Group is required'),
  service_id: Yup.number().nullable(),
  bundle_id: Yup.number().nullable(),
  discount_type: Yup.string().oneOf(['percentage', 'fixed']).required('Discount type is required'),
  discount_value: Yup.number().required('Discount value is required').min(0, 'Discount value must be positive'),
});

type Props = {
  isGroupDiscountLoading: boolean;
  groupdiscount: GroupDiscount;
};

const GroupDiscountEditModalForm: FC<Props> = ({ groupdiscount, isGroupDiscountLoading }) => {
  const { setItemIdForUpdate } = useListView();
  const { refetch } = useQueryResponse();

  const [resellerGroups, setResellerGroups] = useState([]);
  const [services, setServices] = useState([]);
  const [bundles, setBundles] = useState([]);

  const API_URL = process.env.REACT_APP_THEME_API_URL;

  useEffect(() => {
    axios.get(`${API_URL}/admin/reseller-groups`).then((response) => {
      if (response.data.success) {
        setResellerGroups(response.data.data.reseller_groups);
      }
    });

    axios.get(`${API_URL}/admin/services`).then((response) => {
      if (response.data.success) {
        setServices(response.data.data.services);
      }
    });

    axios.get(`${API_URL}/admin/bundles`).then((response) => {
      if (response.data.success) {
        setBundles(response.data.data.bundles);
      }
    });
  }, [API_URL]);

  const cancel = (withRefresh?: boolean) => {
    if (withRefresh) {
      refetch();
    }
    setItemIdForUpdate(undefined);
  };

  const formik = useFormik({
    initialValues: {
      id: groupdiscount.id || '',
      reseller_group_id: groupdiscount.reseller_group_id || '',
      service_id: groupdiscount.service_id || '',
      bundle_id: groupdiscount.bundle_id || '',
      discount_type: groupdiscount.discount_type || 'percentage',
      discount_value: groupdiscount.discount_value || 0,
    },
    validationSchema: editGroupDiscountSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      try {
        if (values.id) {
          await updateGroupDiscount(values);
        } else {
          await createGroupDiscount(values);
        }
      } catch (error) {
        console.error('Error submitting form:', error);
      } finally {
        setSubmitting(false);
        cancel(true);
      }
    },
  });

  return (
    <>
      <form id="kt_modal_add_groupdiscount_form" className="form" onSubmit={formik.handleSubmit} noValidate>
        <div className="d-flex flex-column scroll-y me-n7 pe-7" id="kt_modal_add_groupdiscount_scroll">
          <div className="row">
            {/* Reseller Group */}
            <div className="col-lg-6 fv-row mb-7">
              <label className="required fw-bold fs-6 mb-2">
                <FormattedMessage id="GROUPDISCOUNT.FORM.INPUT.RESELLERGROUP" defaultMessage="Reseller Group" />
              </label>
              <select
                {...formik.getFieldProps('reseller_group_id')}
                className={clsx('form-select form-control', {
                  'is-invalid': formik.touched.reseller_group_id && formik.errors.reseller_group_id,
                  'is-valid': formik.touched.reseller_group_id && !formik.errors.reseller_group_id,
                })}
                disabled={formik.isSubmitting || isGroupDiscountLoading}
              >
                <option value="" disabled>
                  -- Select Reseller Group --
                </option>
                {resellerGroups.map((group:ResellerGroup) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </select>
              {formik.touched.reseller_group_id && formik.errors.reseller_group_id && (
                <div className="invalid-feedback">{formik.errors.reseller_group_id as string}</div>
              )}
            </div>

            {/* Service */}
            <div className="col-lg-6 fv-row mb-7">
              <label className="fw-bold fs-6 mb-2">
                <FormattedMessage id="GROUPDISCOUNT.FORM.INPUT.SERVICE" defaultMessage="Service" />
              </label>
              <select
                {...formik.getFieldProps('service_id')}
                className={clsx('form-select form-control', {
                  'is-invalid': formik.touched.service_id && formik.errors.service_id,
                  'is-valid': formik.touched.service_id && !formik.errors.service_id,
                })}
                disabled={formik.isSubmitting || isGroupDiscountLoading}
              >
                <option value="" disabled>
                  -- Select Service --
                </option>
                {services.map((service:Service) => (
                  <option key={service.id} value={service.id}>
                    {service.service_category?.category_name} - {service.company?.company_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Bundle */}
            <div className="col-lg-6 fv-row mb-7">
              <label className="fw-bold fs-6 mb-2">
                <FormattedMessage id="GROUPDISCOUNT.FORM.INPUT.BUNDLE" defaultMessage="Bundle" />
              </label>
              <select
                {...formik.getFieldProps('bundle_id')}
                className={clsx('form-select form-control', {
                  'is-invalid': formik.touched.bundle_id && formik.errors.bundle_id,
                  'is-valid': formik.touched.bundle_id && !formik.errors.bundle_id,
                })}
                disabled={formik.isSubmitting || isGroupDiscountLoading}
              >
                <option value="" disabled>
                  -- Select Bundle --
                </option>
                {bundles.map((bundle:Bundle) => (
                  <option key={bundle.id} value={bundle.id}>
                    {bundle.bundle_title} - {bundle.service?.company?.company_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Discount Type */}
            <div className="col-lg-6 fv-row mb-7">
              <label className="required fw-bold fs-6 mb-2">
                <FormattedMessage id="GROUPDISCOUNT.FORM.INPUT.DISCOUNTTYPE" defaultMessage="Discount Type" />
              </label>
              <select
                {...formik.getFieldProps('discount_type')}
                className={clsx('form-select form-control', {
                  'is-invalid': formik.touched.discount_type && formik.errors.discount_type,
                  'is-valid': formik.touched.discount_type && !formik.errors.discount_type,
                })}
                disabled={formik.isSubmitting || isGroupDiscountLoading}
              >
                <option value="percentage">Percentage</option>
                <option value="fixed">Fixed</option>
              </select>
            </div>

            {/* Discount Value */}
            <div className="col-lg-6 fv-row mb-7">
              <label className="required fw-bold fs-6 mb-2">
                <FormattedMessage id="GROUPDISCOUNT.FORM.INPUT.DISCOUNTVALUE" defaultMessage="Discount Value" />
              </label>
              <input
                {...formik.getFieldProps('discount_value')}
                className={clsx('form-control form-control-solid mb-3 mb-lg-0', {
                  'is-invalid': formik.touched.discount_value && formik.errors.discount_value,
                  'is-valid': formik.touched.discount_value && !formik.errors.discount_value,
                })}
                type="number"
                autoComplete="off"
                disabled={formik.isSubmitting || isGroupDiscountLoading}
              />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="text-center pt-15">
          <button
            type="reset"
            onClick={() => cancel()}
            className="btn btn-light me-3"
            disabled={formik.isSubmitting || isGroupDiscountLoading}
          >
            <FormattedMessage id="FORM.GENERAL.DISCARD" defaultMessage="Discard" />
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isGroupDiscountLoading || formik.isSubmitting || !formik.isValid || !formik.touched}
          >
            <span className="indicator-label">
              <FormattedMessage id="FORM.GENERAL.SUBMIT" defaultMessage="Submit" />
            </span>
            {formik.isSubmitting && (
              <span className="indicator-progress">
                Please wait...
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            )}
          </button>
        </div>
      </form>
      {isGroupDiscountLoading && <GroupDiscountsListLoading />}
    </>
  );
};

export { GroupDiscountEditModalForm };
